import { BatmanService, LogService, Select } from '@hub/shared'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface InputBankProps {
  label: string
  disabled: boolean
  readOnly: boolean
  errorSpan?: boolean
  required?: boolean
  externalError?: string
  errorIcon?: any
  id: string
  name: string
  leftLabel?: string
  rightLabel?: string
  externalIcon?: any
  value: string
  placeholder?: string
  backgroundColor?: string
  className: string
  onChange?: (value: any) => any
  requiredOnLoad?: boolean
}

export default ({
  label = '',
  disabled = false,
  readOnly = false,
  errorSpan = true,
  required = false,
  externalError,
  errorIcon = <i className="fa-regular fa-triangle-exclamation"></i>,
  id = '',
  name = '',
  leftLabel = '',
  rightLabel = '',
  externalIcon,
  value = '',
  placeholder = '',
  backgroundColor = '',
  className = '',
  onChange,
  requiredOnLoad = false,
}: InputBankProps) => {
  const batmanService = useMemo(() => new BatmanService(), [])
  const logService = useMemo(() => new LogService(), [])
  logService.init('Input Bank')

  const { t } = useTranslation('inputRecordEmployment')
  const [validation, setValidation] = useState(false)
  const [loading, setLoading] = useState(true)
  const [state, setState] = useState({
    value: value,
    error: '',
  })
  const [banksList, setBanksList] = useState<
    Array<{
      code: string
      name: string
    }>
  >([])

  useEffect(() => {
    batmanService
      .getBanks()
      .then((banks) => {
        setBanksList(banks.banks)
        setLoading(false)
      })

      .catch((err) => {
        logService.error({ message: 'Fail to get Banks' })
      })
  }, [batmanService, logService])

  useEffect(() => {
    if (value !== state.value) {
      handleChange(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.value, value])

  const handleChange = useCallback(
    (value: string): void => {
      state.value = value
      if (externalError) {
        state.error = ''
      } else if (!!required && (!state.value || state.value.length < 1)) {
        state.error = t('required')
      } else {
        state.error = ''
      }

      setState({ ...state })
      if (onChange && typeof onChange === 'function' && banksList.length > 0) {
        const result = banksList.find((e) => e.code === state.value)?.name
        onChange(`${state.value},${result}`)
      }
    },
    [banksList, state, externalError, required, onChange, t]
  )

  if (!banksList || banksList?.length === 0) {
    return null
  }

  return (
    <Select
      label={label}
      placeholder={placeholder || '-'}
      value={value}
      className={className}
      name={name}
      errorIcon={errorIcon}
      id={id}
      loading={loading}
      onChange={handleChange}
      onValidate={(value: boolean) => setValidation(value)}
      disabled={disabled || loading}
      readOnly={readOnly}
      required={required}
      errorSpan={errorSpan}
      externalError={externalError ? externalError : state.error}
      leftLabel={leftLabel}
      rightLabel={rightLabel}
      externalIcon={externalIcon}
      backgroundColor={backgroundColor}
      requiredOnLoad={requiredOnLoad}
    >
      {banksList?.map(({ name, code }, index) => (
        <option key={index} value={code}>
          {code} - {name}
        </option>
      ))}
    </Select>
  )
}
